import React, { useState } from "react";
import { consumePendingInventoryMember } from "../state/pendingInventoryOpen.js";
import { SettingsUI } from "./SettingsUI.js";
import { SaveLoadScreen } from "./SaveLoadScreen.js";
import { HelpScreen } from "./HelpScreen.js";
import { InventoryScreen } from "./InventoryScreen.js";
import { DevMapSelector } from "./DevMapSelector.js";
import { FormationScreen } from "./FormationScreen.js";
import { getActiveAdventure } from "../../engine/adventure/ActiveAdventure.js";
import {
  DIFFICULTY_ORDER,
  DIFFICULTY_TUNING,
  getDifficulty,
  setDifficulty,
} from "../../engine/combat/difficulty.js";
import { getSaveInputProvider } from "../../engine/persistence/saveInputRef.js";
import { gameActor } from "../hooks/useGameMachine.js";
import type { LoadSaveFileResult } from "../../engine/persistence/SaveFile.js";

interface Props {
  onClose: () => void;
}

/**
 * MainMenu — pause/escape overlay.
 *
 * Pulls the save-input provider from `saveInputRef` (module singleton set
 * by `main.tsx`) so every mount site (mobile App, DesktopHUD) gets the
 * Save / Load entry without threading props. Post-load, reissues
 * `LOAD_MAP` with the restored pointer so the scene rebuilds on the saved
 * floor.
 */
export function MainMenu({ onClose }: Props) {
  const saveInputProvider = getSaveInputProvider();
  const onLoadComplete = (result: LoadSaveFileResult): void => {
    gameActor.send({ type: "LOAD_MAP", mapId: result.file.state.pointer.mapId });
    onClose();
  };
  const [showSettings, setShowSettings] = useState(false);
  const [showSaveLoad, setShowSaveLoad] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [pendingMember] = useState(() => consumePendingInventoryMember());
  const [showInventory, setShowInventory] = useState(pendingMember !== null);
  const inventoryMemberId = pendingMember ?? undefined;
  const [showMapSelector, setShowMapSelector] = useState(false);
  const [showFormation, setShowFormation] = useState(false);
  const [difficulty, setDifficultyUi] = useState(() => getDifficulty());

  const devMaps = import.meta.env.DEV
    ? (getActiveAdventure().manifest.maps ?? [])
    : [];

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        pointerEvents: "auto",
        zIndex: 200,
      }}
    >
      <div
        style={{
          background: "#1a1a1a",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: 8,
          padding: "32px 48px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          minWidth: 280,
        }}
      >
        {showSettings ? (
          <SettingsUI onBack={() => setShowSettings(false)} />
        ) : showSaveLoad && saveInputProvider ? (
          <SaveLoadScreen
            onBack={() => setShowSaveLoad(false)}
            inputProvider={saveInputProvider}
            onLoadComplete={onLoadComplete}
          />
        ) : showHelp ? (
          <HelpScreen onBack={() => setShowHelp(false)} />
        ) : showInventory ? (
          <InventoryScreen
            onBack={() => setShowInventory(false)}
            {...(inventoryMemberId ? { initialMemberId: inventoryMemberId } : {})}
          />
        ) : showMapSelector ? (
          <DevMapSelector
            maps={devMaps}
            onClose={() => {
              setShowMapSelector(false);
              onClose();
            }}
            onBack={() => setShowMapSelector(false)}
          />
        ) : showFormation ? (
          <FormationScreen onBack={() => setShowFormation(false)} />
        ) : (
          <>
            <h2
              style={{
                color: "white",
                fontFamily: "monospace",
                margin: 0,
                fontSize: 20,
                letterSpacing: 2,
              }}
            >
              PAUSED
            </h2>

            <MenuButton label="Inventory" onClick={() => setShowInventory(true)} />
            <MenuButton label="Formation" onClick={() => setShowFormation(true)} />

            <div style={{ height: 8 }} />

            {saveInputProvider && (
              <MenuButton label="Save / Load" onClick={() => setShowSaveLoad(true)} />
            )}
            <MenuButton label="Settings" onClick={() => setShowSettings(true)} />
            <MenuButton label="Help" onClick={() => setShowHelp(true)} />
            <div
              style={{
                width: "100%",
                color: "white",
                fontFamily: "monospace",
                fontSize: 13,
                display: "flex",
                flexDirection: "column",
                gap: 4,
                padding: "4px 0",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Difficulty</span>
                <span>{DIFFICULTY_TUNING[difficulty].label}</span>
              </div>
              <input
                type="range"
                min={0}
                max={DIFFICULTY_ORDER.length - 1}
                step={1}
                value={DIFFICULTY_ORDER.indexOf(difficulty)}
                onChange={(e) => {
                  const next = DIFFICULTY_ORDER[Number(e.target.value)];
                  if (next) {
                    setDifficulty(next);
                    setDifficultyUi(next);
                  }
                }}
                style={{ width: "100%" }}
              />
            </div>
            {devMaps.length > 0 && (
              <MenuButton
                label="Dev: Jump to Map"
                onClick={() => setShowMapSelector(true)}
              />
            )}
            <MenuButton
              label="Quit to Title"
              onClick={() => gameActor.send({ type: "SHOW_LAUNCH" })}
            />
            <MenuButton label="Resume" onClick={onClose} />
          </>
        )}
      </div>
    </div>
  );
}

function MenuButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? "#222" : "#333",
        color: disabled ? "#555" : "white",
        border: "1px solid",
        borderColor: disabled ? "#333" : "rgba(255,255,255,0.3)",
        borderRadius: 4,
        padding: "8px 24px",
        fontFamily: "monospace",
        fontSize: 13,
        cursor: disabled ? "default" : "pointer",
        width: "100%",
      }}
    >
      {label}
    </button>
  );
}
